/**
 * Client-side view of "may we show this user the $1 trial?" (PANDA-1500).
 *
 * The bug this exists to kill: the answer used to be a plain boolean, prefetched
 * ONCE on mount and defaulting to false. So "we couldn't find out" was stored as
 * "not eligible" — and since nothing retried, a single failed or slow eligibility
 * read marked an eligible user ineligible for their entire page session. They
 * then hit the credit gate and got the full-price Pro paywall instead of the $1
 * offer. Measured on production data: of 10 eligible free users who exhausted
 * their credits, only 2 were ever shown the trial.
 *
 * A brand-new signup is the worst case, and the most common one: the project page
 * mounts the instant the user lands, so the eligibility read can easily race the
 * auth session being established and come back `no_user`.
 *
 * The fix is to stop conflating two different answers. "The server told us no"
 * and "we don't know yet" are now distinct states, because they deserve
 * different behaviour: a definite no is cached and trusted, an unknown is
 * retried and, at the moment of the credit gate, resolved authoritatively before
 * any modal opens.
 */

import type { TrialIneligibleReason } from "@/lib/trial/eligibility";

/**
 * Reasons the server returns that are a settled NO. Each is a durable fact about
 * the account (or the deliberate holdout slice), so caching it is correct and
 * re-asking would just burn a request.
 *
 * Written as a `satisfies` map rather than a bare string list so that adding a
 * member to TrialIneligibleReason FAILS TO COMPILE until it has been explicitly
 * classified as settled (listed here) or transient (added to the Exclude).
 * The previous hand-written Set had no tie to the union at all.
 */
const DEFINITIVE_INELIGIBLE_REASONS = new Set(
  Object.keys({
    paid_plan: true,
    trial_used: true,
    trial_active: true,
    // A past payment is as durable a fact as a past trial. Omitting it here was
    // the failure mode this map exists to prevent: the reason would fall
    // through to the catch-all below and be filed as "unknown", which makes the
    // mount effect in app/project/[id]/page.tsx retry the eligibility read four
    // times per page load, adds a blocking re-check in front of every credit
    // gate, and reports the user as "we couldn't find out" in
    // trial_gate_evaluated — forever, for an answer that can never change.
    previously_paid: true,
    offer_expired: true,
    suppressed: true,
    holdout: true,
    // Not members of TrialIneligibleReason — these are route-level refusals
    // (the T2 frequency cap and the feature flag) that only ever appear on the
    // wire, so they are listed separately from the `satisfies` contract below.
    frequency_capped: true,
    disabled: true,
    // `no_user` and `billing_unreadable` are deliberately ABSENT. Both mean
    // "we could not find out", not "no": no_user usually means the session
    // cookie wasn't ready, and billing_unreadable is a failed database read.
    // Caching either would mark a genuinely eligible user ineligible for the
    // rest of their session on one transient blip — the original bug this
    // module exists to prevent.
  } satisfies Record<
    | Exclude<TrialIneligibleReason, "no_user" | "billing_unreadable">
    | "frequency_capped"
    | "disabled",
    true
  >),
);

/**
 * Reasons that are NOT settled. `no_user` is the important one: it usually means
 * the session cookie wasn't ready yet, not that the person is ineligible —
 * treating it as a no is precisely the bug above.
 */
export function isDefinitiveIneligibility(reason: unknown): boolean {
  return typeof reason === "string" && DEFINITIVE_INELIGIBLE_REASONS.has(reason);
}

export type TrialOfferStatus =
  /** The server said yes and gave us an offer to render. */
  | "eligible"
  /** The server gave a settled no. Trust it; don't re-ask. */
  | "ineligible"
  /** We could not find out (network error, non-2xx, session not ready yet). */
  | "unknown";

export interface TrialOfferState<TOffer = unknown> {
  status: TrialOfferStatus;
  offer: TOffer | null;
  /** Server-provided reason, kept for logging so QA can see WHY. */
  reason?: string;
  /** Copy A/B arm from the same read (lib/billing/upgrade-copy-experiment).
   *  Carried on the state so the credit-gate path — which forwards a prefetched
   *  offer straight to the modal and deliberately never re-checks — renders the
   *  same arm the server assigned, instead of falling back to control. */
  copyVariant?: string;
  /** Free-plan project usage from the same read, for the "N/3 projects used"
   *  chip. Absent when the server couldn't count them. */
  projects?: { current: number; limit: number } | null;
  /**
   * The card-first gate's answer for this account (PANDA-2375).
   *
   * Carried on EVERY status, not only `eligible`: the legacy $1 offer can be
   * expired, frequency-capped or switched off for a user the card-first gate
   * still requires a payment method from, and that user must meet the card-first
   * modal rather than a plans picker. Reading it off the same payload keeps one
   * server read behind both decisions.
   */
  cardFirst?: CardFirstOfferState | null;
}

/** The card-first half of an eligibility read. `terms` is whatever the server
 *  sent, passed through untouched — the modal owns its shape, and inventing a
 *  second copy of the price here is exactly what the card-first work forbids. */
export interface CardFirstOfferState {
  required: boolean;
  terms: unknown;
}

export interface EligibilityResponse {
  eligible?: unknown;
  reason?: unknown;
  offer?: unknown;
  copyVariant?: unknown;
  projects?: unknown;
  card_required?: unknown;
  card_first_trial?: unknown;
}

/**
 * Narrow the card-first fields off the wire.
 *
 * Both halves are required before this reports `required: true`: a gate with no
 * terms has nothing honest to render, and the caller must fall through to its
 * existing surface rather than open a modal with an invented price.
 */
function readCardFirst(body: EligibilityResponse): CardFirstOfferState | null {
  if (body.card_required !== true) return null;
  const terms = body.card_first_trial;
  if (!terms || typeof terms !== "object") return null;
  return { required: true, terms };
}

/** Narrow the loosely-typed `projects` field off the wire. */
function readProjects(v: unknown): { current: number; limit: number } | null {
  if (!v || typeof v !== "object") return null;
  const p = v as { current?: unknown; limit?: unknown };
  return typeof p.current === "number" && typeof p.limit === "number"
    ? { current: p.current, limit: p.limit }
    : null;
}

/**
 * Turn one eligibility read into a state.
 *
 * `ok` is whether the request itself completed with a 2xx — a failed fetch or a
 * 5xx must land on `unknown`, never on `ineligible`, or we're back to silently
 * downgrading eligible users to the full-price paywall on a transient blip.
 *
 * An `eligible: true` with no offer body is treated as unknown rather than
 * eligible: we'd have nothing to render, and re-asking is better than opening an
 * empty modal.
 */
export function interpretTrialEligibility<TOffer>(input: {
  ok: boolean;
  body: EligibilityResponse | null | undefined;
}): TrialOfferState<TOffer> {
  if (!input.ok || !input.body) {
    return { status: "unknown", offer: null, reason: "request_failed" };
  }

  const { eligible, reason, offer } = input.body;
  const cardFirst = readCardFirst(input.body);

  if (eligible === true) {
    if (offer) {
      return {
        status: "eligible",
        offer: offer as TOffer,
        reason: typeof reason === "string" ? reason : undefined,
        copyVariant:
          typeof input.body.copyVariant === "string"
            ? input.body.copyVariant
            : undefined,
        projects: readProjects(input.body.projects),
        cardFirst,
      };
    }
    return {
      status: "unknown",
      offer: null,
      reason: "eligible_without_offer",
      cardFirst,
    };
  }

  if (isDefinitiveIneligibility(reason)) {
    return {
      status: "ineligible",
      offer: null,
      reason: reason as string,
      cardFirst,
    };
  }

  // Anything else — `no_user`, an unrecognised/new reason, a malformed body —
  // is an unknown. Fail towards ASKING AGAIN, not towards charging full price.
  return {
    status: "unknown",
    offer: null,
    reason: typeof reason === "string" ? reason : "unrecognised",
    cardFirst,
  };
}

/**
 * Whether it's worth spending a round-trip to resolve this state before showing
 * a monetization surface. Only `unknown` qualifies — an eligible user already
 * has their offer, and a definite no won't change by asking twice.
 */
export function needsAuthoritativeCheck(state: {
  status: TrialOfferStatus;
}): boolean {
  return state.status === "unknown";
}
